// Muscle highlight for the demo figure: each vertex of the shared human gets a body region from its main bone and
// where it sits in the standing rest pose (figure faces +z, y up, meters). Coarse on purpose: it shows which part
// of the body works, not anatomy. Pure functions, tested in muscles.test.ts.

export const REGIONS = ['none', 'quads', 'hamstrings', 'glutes', 'calves', 'core', 'lowerBack', 'upperBack', 'chest', 'shoulders', 'biceps', 'triceps', 'hipFlexors', 'forearms'] as const;
export type Region = (typeof REGIONS)[number];

/** Region of one vertex from its main bone (three.js name, for example `upperleg01L`), rest position and normal. */
export function regionOf(bone: string, y: number, nz: number, x = 0): Region {
  const front = nz > 0.15, back = nz < -0.15;
  if (bone.startsWith('upperleg')) {
    if (back) return y > 0.8 ? 'glutes' : 'hamstrings';
    if (front && y > 0.82) return 'hipFlexors';
    return 'quads';
  }
  if (bone.startsWith('pelvis')) return back ? 'glutes' : Math.abs(x) > 0.07 ? 'hipFlexors' : 'core';
  if (bone.startsWith('lowerleg')) return back ? 'calves' : 'none';
  if (/^spine0[345]$/.test(bone)) return back ? 'lowerBack' : 'core';
  if (/^spine0[12]$/.test(bone)) {
    if (back) return y > 1.12 ? 'upperBack' : 'lowerBack';
    if (front) return y > 1.25 ? 'chest' : 'core';
    return y > 1.25 ? 'upperBack' : 'core';
  }
  if (bone.startsWith('breast')) return 'chest';
  if (bone.startsWith('clavicle')) return back ? 'upperBack' : 'chest';
  if (bone.startsWith('shoulder')) return 'shoulders';
  if (bone.startsWith('upperarm')) {
    if (y > 1.33) return 'shoulders';
    return nz >= 0 ? 'biceps' : 'triceps';
  }
  if (bone.startsWith('lowerarm')) return 'forearms';
  return 'none';
}

/** Our muscle words (MUSCLES in @fitness/core) to regions of the figure. Words without a place stay unmarked. */
const WORDS: Record<string, Region[]> = {
  chest: ['chest'], 'upper chest': ['chest'],
  shoulders: ['shoulders'], 'front delts': ['shoulders'], 'side delts': ['shoulders'], 'rear delts': ['shoulders'],
  triceps: ['triceps'], biceps: ['biceps'], forearms: ['forearms'], grip: ['forearms'], wrists: ['forearms'],
  back: ['upperBack', 'lowerBack'], 'upper back': ['upperBack'], lats: ['upperBack'], traps: ['upperBack'], 'lower back': ['lowerBack'], spine: ['lowerBack', 'upperBack'],
  core: ['core'], abs: ['core'], obliques: ['core'], 'hip flexors': ['hipFlexors'],
  glutes: ['glutes'], hips: ['glutes', 'hipFlexors'], adductors: ['quads'], abductors: ['glutes'],
  quads: ['quads'], hamstrings: ['hamstrings'], calves: ['calves'],
  'full body': ['quads', 'hamstrings', 'glutes', 'core', 'upperBack', 'shoulders', 'chest'],
};

export function regionsFor(muscles: readonly string[]): Set<Region> {
  const out = new Set<Region>();
  for (const m of muscles) for (const r of WORDS[m.toLowerCase()] ?? []) out.add(r);
  return out;
}
